import React, { useMemo } from "react";
import "./pop_up.css";

const generateConfettiPieces = (count) => {
  return Array.from({ length: count }, (_, index) => ({
    id: `confetti-${index}`,
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 0.9}s`,
    duration: `${2.8 + Math.random() * 1.8}s`,
    colorClass: `confetti-color-${(index % 8) + 1}`,
    rotate: `${Math.floor(Math.random() * 360)}deg`,
    size: Math.random() > 0.6 ? 'large' : 'medium'
  }));
};

const generateStreamers = (count) => {
  return Array.from({ length: count }, (_, index) => ({
    id: `streamer-${index}`,
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 0.7}s`,
    duration: `${2.2 + Math.random() * 1.6}s`,
    rotate: `${-45 + Math.random() * 90}deg`,
    colorClass: `streamer-color-${(index % 8) + 1}`,
    wave: Math.random() > 0.5 ? 'wave-slow' : 'wave-fast'
  }));
};

const generateSparkles = (count) => {
  return Array.from({ length: count }, (_, index) => ({
    id: `sparkle-${index}`,
    left: `${Math.random() * 100}%`,
    top: `${12 + Math.random() * 54}%`,
    delay: `${Math.random() * 0.6}s`,
    duration: `${1.1 + Math.random() * 0.9}s`
  }));
};

const generateBokeh = (count) => {
  return Array.from({ length: count }, (_, index) => ({
    id: `bokeh-${index}`,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    size: `${20 + Math.random() * 90}px`,
    delay: `${Math.random() * 1.1}s`,
    duration: `${3.6 + Math.random() * 2.2}s`,
    opacity: `${0.1 + Math.random() * 0.24}`,
    colorClass: `bokeh-color-${(index % 6) + 1}`
  }));
};

const generateLightStreaks = (count) => {
  return Array.from({ length: count }, (_, index) => ({
    id: `streak-${index}`,
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 0.7}s`,
    duration: `${1.4 + Math.random() * 1.4}s`,
    rotate: `${-32 + Math.random() * 64}deg`
  }));
};

const Popup = ({ onClose, visible, celebrationActive, visitCount }) => {
  const effectsProfile = useMemo(() => {
    if (typeof window === "undefined") {
      return {
        confetti: 180,
        streamers: 44,
        sparkles: 90,
        bokeh: 36,
        streaks: 18
      };
    }

    const isMobileViewport = window.matchMedia("(max-width: 768px)").matches;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reducedMotion) {
      return {
        confetti: 20,
        streamers: 6,
        sparkles: 12,
        bokeh: 8,
        streaks: 2
      };
    }

    if (isMobileViewport) {
      return {
        confetti: 50,
        streamers: 16,
        sparkles: 28,
        bokeh: 12,
        streaks: 6
      };
    }

    return {
      confetti: 180,
      streamers: 44,
      sparkles: 90,
      bokeh: 36,
      streaks: 18
    };
  }, []);

  const confettiPieces = useMemo(() => generateConfettiPieces(effectsProfile.confetti), [effectsProfile.confetti]);
  const streamers = useMemo(() => generateStreamers(effectsProfile.streamers), [effectsProfile.streamers]);
  const sparkles = useMemo(() => generateSparkles(effectsProfile.sparkles), [effectsProfile.sparkles]);
  const bokehLights = useMemo(() => generateBokeh(effectsProfile.bokeh), [effectsProfile.bokeh]);
  const lightStreaks = useMemo(() => generateLightStreaks(effectsProfile.streaks), [effectsProfile.streaks]);

  if (!visible) {
    return null;
  }

  const handleClose = () => {
    onClose();
  };

  return (
    <div 
      className="popup-overlay" 
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div className="popup-content" onClick={(e) => e.stopPropagation()}>
        <div className="visit-counter">
          <span className="counter-label">Visitas:</span>
          <span className="counter-number">{visitCount}</span>
        </div>
        <button 
          onClick={handleClose}
          className="close-btn"
          type="button"
          aria-label="Cerrar anuncio"
        >
          ×
        </button>
        <img
          src="/assets/imgs/Noticias/jerarquia_movilidad.png"
          alt="Jerarquía de la movilidad"
          onError={(e) => {
            e.target.src = "/assets/imgs/Noticias/TC_Conv_Nal_HGO.png";
          }}
          className="popup-image"
        />

        {celebrationActive && (
          <div className="celebration-layer" aria-hidden="true">
            <div className="celebration-aura" />

            {bokehLights.map((bokeh) => (
              <span
                key={bokeh.id}
                className={`bokeh-light ${bokeh.colorClass}`}
                style={{
                  left: bokeh.left,
                  top: bokeh.top,
                  width: bokeh.size,
                  height: bokeh.size,
                  opacity: bokeh.opacity,
                  animationDelay: bokeh.delay,
                  animationDuration: bokeh.duration
                }}
              />
            ))}

            {lightStreaks.map((streak) => (
              <span
                key={streak.id}
                className="light-streak"
                style={{
                  left: streak.left,
                  animationDelay: streak.delay,
                  animationDuration: streak.duration,
                  transform: `rotate(${streak.rotate})`
                }}
              />
            ))}

            {/* Sparkles for explosion effect */}
            {sparkles.map((sparkle) => (
              <div
                key={sparkle.id}
                className="sparkle"
                style={{
                  left: sparkle.left,
                  top: sparkle.top,
                  animationDelay: sparkle.delay,
                  animationDuration: sparkle.duration
                }}
              />
            ))}

            {/* Confetti pieces */}
            {confettiPieces.map((piece) => (
              <span
                key={piece.id}
                className={`confetti-piece ${piece.colorClass} ${piece.size}`}
                style={{
                  left: piece.left,
                  animationDelay: piece.delay,
                  animationDuration: piece.duration,
                  transform: `rotate(${piece.rotate})`
                }}
              />
            ))}

            {/* Streamers */}
            {streamers.map((streamer) => (
              <span
                key={streamer.id}
                className={`streamer ${streamer.colorClass} ${streamer.wave}`}
                style={{
                  left: streamer.left,
                  animationDelay: streamer.delay,
                  animationDuration: streamer.duration,
                  transform: `rotate(${streamer.rotate})`
                }}
              />
            ))}

            {/* Celebration Title with pulse */}
            <div className="celebration-title">
              <span className="pulse-ring"></span>
              ¡Visita #<span className="milestone-number">{visitCount}</span>!
            </div>

            {/* Fireworks center glow */}
            <div className="fireworks-center"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Popup;
